/*
 ** Keeps the Smartrr purchase options widget in sync with the product page variant picker.
 **
 ** - The purchase option the shopper picked (one time purchase or a subscription
 **   frequency) is kept when they switch variant. It only falls back to one time
 **   purchase when the selected subscription is not sold for the new variant.
 ** - One time purchase is selected on page load, unless the shopper arrived with a
 **   selling plan in the URL or the product can only be bought as a subscription.
 */
(function () {
  'use strict';

  var CONFIG_SELECTOR = '[data-smartrr-purchase-option-sync]';
  var GROUP_INPUT_TAG = 'data-smartrr-selling-plan-group-input';
  var SELLING_PLAN_INPUT_TAG = 'data-smartrr-selling-plan-input';
  var WIDGET_SELLING_PLAN_INPUT_TAG = 'data-smartrr-widget-selling-plan-input';
  var ONE_TIME_PURCHASE = '';
  var HANDLER_POLL_INTERVAL = 100;
  var HANDLER_POLL_TIMEOUT = 5000;

  function whenHandlerReady(productId, callback) {
    var waited = 0;

    function poll() {
      var list = window.smartrrProductList;
      var handler = list ? list[productId] : null;

      if (handler && handler.ui && handler.ui.logic) {
        callback(handler);
        return;
      }

      if (waited >= HANDLER_POLL_TIMEOUT) return;

      waited += HANDLER_POLL_INTERVAL;
      setTimeout(poll, HANDLER_POLL_INTERVAL);
    }

    poll();
  }

  /* The widget is rendered by the Smartrr app block, so its position depends on
     what the app was able to use as a form. Fall back to the whole document. */
  function widgetRoot(handler) {
    var form = handler.ui.$form;
    return form && typeof form.querySelectorAll === 'function' ? form : document;
  }

  function hasOneTimePurchase(handler) {
    var inputs = widgetRoot(handler).querySelectorAll('[' + GROUP_INPUT_TAG + ']');

    return Array.prototype.some.call(inputs, function (input) {
      return input.value === ONE_TIME_PURCHASE;
    });
  }

  function selectOneTimePurchase(handler) {
    handler.ui.logic.apiChangeGroup(ONE_TIME_PURCHASE);
    handler.ui.logic.apiChangePlan(ONE_TIME_PURCHASE);
  }

  function findVariantPicker(scopeId) {
    var scope = (scopeId && document.getElementById(scopeId)) || document;
    return scope.querySelector('variant-selects, variant-radios');
  }

  function selectedOptions(picker) {
    var selects = Array.prototype.slice.call(picker.querySelectorAll('select'));

    if (selects.length) {
      return selects.map(function (select) {
        return select.value;
      });
    }

    return Array.prototype.slice.call(picker.querySelectorAll('fieldset')).map(function (fieldset) {
      var checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });
  }

  function resolveVariantId(handler, picker) {
    var options = selectedOptions(picker);
    var variants = handler.ui.logic.apiGetVariants() || [];

    var match = variants.find(function (variant) {
      return (
        variant.options.length === options.length &&
        variant.options.every(function (option, index) {
          return option === options[index];
        })
      );
    });

    if (match) return String(match.id);

    return picker.currentVariant ? String(picker.currentVariant.id) : null;
  }

  function restorePurchaseOption(handler, option, variantId) {
    var logic = handler.ui.logic;

    if (!option.groupId) {
      selectOneTimePurchase(handler);
      return;
    }

    if (!logic.apiGetAnySellingPlanAllocationByVariantGroupId(variantId, option.groupId)) {
      /* The subscription the shopper had selected is not sold for this variant. */
      if (hasOneTimePurchase(handler)) selectOneTimePurchase(handler);
      return;
    }

    logic.apiChangeGroup(option.groupId);

    if (option.planId && logic.apiGetSellingPlanAllocationByVariantGroupPlanId(variantId, option.groupId, option.planId)) {
      logic.apiChangePlan(option.planId);
    }
  }

  function keepPurchaseOptionOnVariantChange(handler, picker) {
    /* Listening on the capture phase reads the purchase option before the Smartrr
       widget and the theme variant picker react to the change. */
    picker.addEventListener(
      'change',
      function () {
        var option = handler.ui.logic.apiGetCurrentCopy();

        setTimeout(function () {
          var variantId = resolveVariantId(handler, picker);

          if (!variantId) return;

          handler.ui.logic.apiChangeVariant(variantId);
          restorePurchaseOption(handler, option, variantId);
        }, 0);
      },
      true
    );
  }

  function mirrorSellingPlanInput(handler) {
    var root = widgetRoot(handler);
    var sellingPlanInput = root.querySelector('[' + SELLING_PLAN_INPUT_TAG + ']');
    var widgetSellingPlanInput = root.querySelector('[' + WIDGET_SELLING_PLAN_INPUT_TAG + ']');

    if (!sellingPlanInput || !widgetSellingPlanInput) return;

    var observer = new MutationObserver(function () {
      widgetSellingPlanInput.value = sellingPlanInput.value;
    });

    observer.observe(sellingPlanInput, { attributes: true, childList: true, subtree: true, characterData: true });
  }

  function init(config) {
    var productId = config.getAttribute('data-product-id');

    if (!productId) return;

    whenHandlerReady(productId, function (handler) {
      if (
        config.getAttribute('data-default-one-time-purchase') === 'true' &&
        !new URLSearchParams(window.location.search).has('selling_plan') &&
        hasOneTimePurchase(handler)
      ) {
        selectOneTimePurchase(handler);
      }

      var picker = findVariantPicker(config.getAttribute('data-scope-id'));

      if (picker) keepPurchaseOptionOnVariantChange(handler, picker);

      mirrorSellingPlanInput(handler);
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll(CONFIG_SELECTOR), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
